import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Link2, X, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import GalleryPicker from '../video-studio/GalleryPicker';

export interface AIImageReferenceValue {
  enabled: boolean;
  url: string | null; // ready-to-send URL (post-upload if upload mode)
}

export interface AIImageReferenceHandle {
  /**
   * Resolve the current reference to a URL.
   * - upload mode: uploads the file via uploadReference and returns the URL
   * - url mode: returns the current URL string
   * Returns null when reference is disabled or empty.
   * Throws if upload fails.
   */
  resolve: () => Promise<string | null>;
  /** Reset to the disabled/empty state. */
  clear: () => void;
}

export interface AIImageReferenceInputProps {
  /** Upload a File → return a { url } object. Required when mode is 'upload'. */
  uploadReference: (file: File) => Promise<{ url: string }>;
  /** Pre-fill reference URL (e.g. from a gallery "use as reference" action). */
  initialUrl?: string | null;
  /** Called when the initial URL has been consumed. */
  onInitialConsumed?: () => void;
  /** Called whenever the user toggles the checkbox or changes data. */
  onChange?: (state: { enabled: boolean; hasValue: boolean }) => void;
}

const AIImageReferenceInput = forwardRef<AIImageReferenceHandle, AIImageReferenceInputProps>(function AIImageReferenceInput(
  { uploadReference, initialUrl, onInitialConsumed, onChange },
  ref
) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<'upload' | 'url' | 'library'>('upload');
  const [showLibrary, setShowLibrary] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (initialUrl) {
      setEnabled(true);
      setMode('url');
      setUrl(initialUrl);
      setFile(null);
      setPreview(initialUrl);
      onInitialConsumed?.();
      onChange?.({ enabled: true, hasValue: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl]);

  const notify = (nextEnabled: boolean, hasValue: boolean) => {
    onChange?.({ enabled: nextEnabled, hasValue });
  };

  const clearAll = () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    notify(enabled, false);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error(t('imageStudio.reference.onlyImages', 'Only image files are allowed.'));
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error(t('imageStudio.reference.tooBig', 'Reference image must be under 10 MB.'));
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    notify(enabled, true);
  };

  useImperativeHandle(ref, () => ({
    async resolve() {
      if (!enabled) return null;
      if (mode === 'upload') {
        if (!file) return null;
        setUploading(true);
        try {
          const uploaded = await uploadReference(file);
          return uploaded?.url || null;
        } finally {
          setUploading(false);
        }
      }
      // Both 'url' and 'library' modes use the `url` state as the resolved value.
      const trimmed = url.trim();
      return trimmed.length > 0 ? trimmed : null;
    },
    clear() {
      setEnabled(false);
      clearAll();
    }
  }));

  return (
    <div className="rounded-lg bg-white/60 dark:bg-gray-900/40 border border-purple-200 dark:border-purple-800 p-3">
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            const on = e.target.checked;
            setEnabled(on);
            if (!on) clearAll();
            notify(on, mode === 'upload' ? !!file : url.trim().length > 0);
          }}
          className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <div>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {t('imageStudio.reference.enable', 'Usar imagem de referência')}
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('imageStudio.reference.hint', 'A IA vai se basear nela para gerar a nova imagem.')}
          </p>
        </div>
      </label>

      {enabled && (
        <div className="mt-3 space-y-2">
          <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                mode === 'upload'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Upload className="h-3.5 w-3.5" />
              {t('imageStudio.reference.upload', 'Upload')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('library'); setShowLibrary(true); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-gray-200 dark:border-gray-700 ${
                mode === 'library'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <ImageIcon className="h-3.5 w-3.5" />
              {t('imageStudio.reference.fromLibrary', 'Da biblioteca')}
            </button>
            <button
              type="button"
              onClick={() => setMode('url')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-gray-200 dark:border-gray-700 ${
                mode === 'url'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Link2 className="h-3.5 w-3.5" />
              {t('imageStudio.reference.pasteUrl', 'Colar link')}
            </button>
          </div>

          {mode === 'upload' ? (
            <div>
              {!preview ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-3 py-4 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 hover:border-purple-400 hover:text-purple-600 transition-colors"
                >
                  {t('imageStudio.reference.clickToUpload', 'Clique para escolher uma imagem (até 10 MB)')}
                </button>
              ) : (
                <div className="relative inline-block">
                  <img src={preview} alt="reference" className="h-24 rounded-md border border-gray-200 dark:border-gray-700 object-cover" />
                  <button
                    type="button"
                    onClick={clearAll}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 shadow"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
              />
            </div>
          ) : mode === 'library' ? (
            <div>
              {!url ? (
                <button
                  type="button"
                  onClick={() => setShowLibrary(true)}
                  className="w-full px-3 py-4 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 hover:border-purple-400 hover:text-purple-600 transition-colors"
                >
                  {t('imageStudio.reference.pickFromLibrary', 'Clique para escolher uma imagem da sua biblioteca')}
                </button>
              ) : (
                <div className="relative inline-block">
                  <img src={url} alt="reference" className="h-24 rounded-md border border-gray-200 dark:border-gray-700 object-cover" />
                  <button
                    type="button"
                    onClick={clearAll}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 shadow"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setPreview(e.target.value.trim() || null);
                  notify(enabled, e.target.value.trim().length > 0);
                }}
                placeholder={t('imageStudio.reference.urlPlaceholder', 'https://…/imagem.jpg') as string}
                className="w-full px-2 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {url.trim() && (
                <div className="relative inline-block">
                  <img
                    src={url}
                    alt="Reference"
                    className="h-24 rounded-md border border-gray-200 dark:border-gray-700 object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={clearAll}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 shadow"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}
          {uploading && (
            <p className="text-xs text-purple-600">{t('imageStudio.reference.uploading', 'Subindo imagem de referência...')}</p>
          )}
        </div>
      )}

      <GalleryPicker
        open={showLibrary}
        source="image"
        onClose={() => setShowLibrary(false)}
        onPick={(asset) => {
          const pickedUrl = asset.url || asset.thumbnail_url || '';
          if (!pickedUrl) {
            toast.error(t('imageStudio.reference.assetMissingUrl', 'Esse item não tem URL disponível') as string);
            return;
          }
          setMode('library');
          setUrl(pickedUrl);
          setPreview(pickedUrl);
          setFile(null);
          setShowLibrary(false);
          notify(enabled, true);
        }}
      />
    </div>
  );
});

export default AIImageReferenceInput;
